import React, { ClipboardEventHandler, KeyboardEventHandler, useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

import { useDelayedUnmount } from 'src/common/spotlight/use-delayed-unmount.hook';
import { IconButton } from 'src/common/icon-button';
import { RightStrongIcon } from 'src/monet/icons';
import { formatUrl } from 'src/utils/url';

import { FormikTextInput } from 'src/common/form/formik/text-input';
import { useSharePanelStore } from 'src/common/store/share.store';
import { JourneyAliasFormType } from '../../form/link-config-form.factory';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { checkInputForUrl } from 'src/common/helpers/check-input-for-url';
import { ShareLinkPersonalizationBanner } from './personalization-banner';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { useProspectLogo } from 'src/common/hooks/use-prospect-logo.hook';

const ShareLinkNameInput = ({
  isNeueActive = false,
}: {
  isNeueActive: boolean;
}) => {
  const { setErrorNotification } = useNotificationMessage();
  const setName = useSharePanelStore((state: any) => state.setAliasName);
  const isNewAlias = useSharePanelStore((state: any) => state.isNewAlias);

  const linkNamePlaceholder = isNeueActive ? 'Name' : 'Recipient domain or name';

  const {
    values: { uuid, name, personalization_domain },
    setFieldValue,
  } = useFormikContext<JourneyAliasFormType>();

  const debouncedSetName = useMemo(() => debounce((value: string) => setName(value), 500), [setName]);

  // Stop the invocation of the debounced function after unmounting
  useEffect(() => {
    return () => {
      debouncedSetName.cancel();
    };
  }, [debouncedSetName]);

  useEffect(() => {
    debouncedSetName(name);
  }, [name, debouncedSetName]);

  const isValidUrl = !!name && Boolean(checkInputForUrl(name));
  const { mounted: mountedEnterButton } = useDelayedUnmount(!isValidUrl);
  const { fetchProspectLogo, logoUrl, domain, generatedSlug, fetching: isFetchingBrand, status } = useProspectLogo();

  useEffect(() => {
    if (status === 'success') {
      setFieldValue('personalization_logo_url', logoUrl);
      setFieldValue('personalization_domain', domain);

      // only apply the generated slug if this is a new alias creation
      if (isNewAlias && generatedSlug) {
        setFieldValue('slug', generatedSlug);
      }
    }
  }, [generatedSlug, isNewAlias, logoUrl, domain, status, setFieldValue]);

  const onUrlProcessed = async (url: string) => {
    try {
      const formattedUrl = formatUrl(url);
      const domain = new URL(formattedUrl).hostname;

      await fetchProspectLogo(domain);
    } catch (error: any) {
      setErrorNotification(error.message);
    }
  };

  const processInputValue = (value: string = name) => {
    if (isNeueActive) return;

    const url = checkInputForUrl(value);

    if (url && value !== personalization_domain) {
      onUrlProcessed(url);
    }
  };

  const onProcessButtonClicked = () => {
    processInputValue();
  };

  const onKeyDowned: KeyboardEventHandler = (event) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      processInputValue();
    }
  };

  const onInputPasted: ClipboardEventHandler = (event) => {
    const text = event.clipboardData.getData('text');
    processInputValue(text);
  };

  return (
    <div className='flex flex-col space-y-2 pb-6 border-b'>
      <FormikTextInput
        maxLength={50}
        hideCharacterCount
        id='journey-share-alias-form-name'
        name='name'
        required
        label='Name link'
        placeholder={linkNamePlaceholder}
        type='text'
        autoComplete='off'
        autoCapitalize='off'
        autoCorrect='off'
        spellCheck='false'
        onKeyDown={onKeyDowned}
        onPaste={onInputPasted}
        onBlur={(e: any) => processInputValue(e.target.value)}
        autoFocus={!uuid}
        trailingAddon={!isNeueActive ? (
          <div
            className={classNames('transition-opacity opacity-0', {
              invisible: !mountedEnterButton,
              visible: mountedEnterButton,
              'opacity-100': !isValidUrl,
            })}
          >
            {isFetchingBrand ? (
              <LoadingSpinner />
            ) : (
              <IconButton onClick={onProcessButtonClicked} onKeyDown={onKeyDowned}>
                <RightStrongIcon className='text-bedrock-gray-medium group-hover:text-bedrock-brand-secondary group-focus:text-bedrock-brand-secondary group-focus-visible:text-bedrock-brand-secondary group-focus-within:text-bedrock-brand-secondary' />
              </IconButton>
            )}
          </div>
          ): null
        }
      />
      {!isNeueActive && (
        <ShareLinkPersonalizationBanner />
      )}
    </div>
  );
};

export default ShareLinkNameInput;
